import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient as api, resetRefreshAttempts, setApiAccessToken, bumpAuthGeneration } from '../services/apiService';

// localStorage key for session timeout modal persistence
const SESSION_TIMEOUT_MODAL_KEY = 'sessionTimeoutModalShown';
// localStorage key for session timeout timestamp
const SESSION_TIMEOUT_TIMESTAMP_KEY = 'sessionTimeoutTimestamp';
// Maximum age of session expiration to show modal (30 minutes in milliseconds)
const SESSION_TIMEOUT_MAX_AGE = 30 * 60 * 1000; // 30 minutes

// TC-AUTH-008: localStorage keys for concurrent session handling
const CONCURRENT_SESSION_KEY = 'concurrentSessionDetected';
const CONCURRENT_SESSION_MESSAGE_KEY = 'concurrentSessionMessage';

// localStorage key for storing redirect URL after session timeout
const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';

const normalizeRedirectAfterLogin = (url) => {
  if (!url) return url;

  const pathOnly = String(url).split(/[?#]/)[0];
  if (/^\/invoices\/edit\/[^/]+$/.test(pathOnly)) {
    return '/invoices/pending';
  }

  return url;
};

// Helper function to check if session timeout modal should be shown from localStorage
const getSessionTimeoutModalFromStorage = () => {
  try {
    return localStorage.getItem(SESSION_TIMEOUT_MODAL_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

// Helper function to set session timeout modal flag in localStorage
const setSessionTimeoutModalInStorage = (value) => {
  try {
    if (value) {
      localStorage.setItem(SESSION_TIMEOUT_MODAL_KEY, 'true');
      // Also store the timestamp when session expired
      localStorage.setItem(SESSION_TIMEOUT_TIMESTAMP_KEY, Date.now().toString());
    } else {
      localStorage.removeItem(SESSION_TIMEOUT_MODAL_KEY);
      localStorage.removeItem(SESSION_TIMEOUT_TIMESTAMP_KEY);
    }
  } catch (error) {
    console.error('Error setting session timeout modal in localStorage:', error);
  }
};

// Helper function to check if session expiration is recent (within last 30 minutes)
// Returns true if the session expired recently and modal should be shown
export const isSessionExpirationRecent = () => {
  try {
    const timestamp = localStorage.getItem(SESSION_TIMEOUT_TIMESTAMP_KEY);
    if (!timestamp) {
      // If no timestamp but flag is set, consider it not recent (stale)
      return false;
    }
    
    const expirationTime = parseInt(timestamp, 10);
    const now = Date.now();
    const age = now - expirationTime;
    
    // Only consider recent if within last 30 minutes
    return age >= 0 && age <= SESSION_TIMEOUT_MAX_AGE;
  } catch (error) {
    console.error('Error checking session expiration timestamp:', error);
    return false;
  }
};

// Helper function to clear stale session timeout flags
export const clearStaleSessionTimeout = () => {
  try {
    localStorage.removeItem(SESSION_TIMEOUT_MODAL_KEY);
    localStorage.removeItem(SESSION_TIMEOUT_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing session timeout flags:', error);
  }
};

export const clearSessionTimeoutState = () => {
  setSessionTimeoutModalInStorage(false);
};

// TC-AUTH-008: Helper function to check if concurrent session was detected
export const getConcurrentSessionFromStorage = () => {
  try {
    return localStorage.getItem(CONCURRENT_SESSION_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

// TC-AUTH-008: Helper function to get concurrent session message
export const getConcurrentSessionMessage = () => {
  try {
    return localStorage.getItem(CONCURRENT_SESSION_MESSAGE_KEY) || 'Logged in from another device. Please log in again.';
  } catch (error) {
    return 'Logged in from another device. Please log in again.';
  }
};

// TC-AUTH-008: Helper function to clear concurrent session flags
export const clearConcurrentSession = () => {
  try {
    localStorage.removeItem(CONCURRENT_SESSION_KEY);
    localStorage.removeItem(CONCURRENT_SESSION_MESSAGE_KEY);
  } catch (error) {
    console.error('Error clearing concurrent session flags:', error);
  }
};

// Helper function to set redirect URL after login
export const setRedirectAfterLogin = (url) => {
  try {
    if (url && url !== '/login' && !url.startsWith('/login')) {
      localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, normalizeRedirectAfterLogin(url));
    }
  } catch (error) {
    console.error('Error setting redirect URL:', error);
  }
};

// Helper function to get redirect URL after login
export const getRedirectAfterLogin = () => {
  try {
    return normalizeRedirectAfterLogin(localStorage.getItem(REDIRECT_AFTER_LOGIN_KEY));
  } catch (error) {
    console.error('Error getting redirect URL:', error);
    return null;
  }
};

// Helper function to clear redirect URL after login
export const clearRedirectAfterLogin = () => {
  try {
    localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
  } catch (error) {
    console.error('Error clearing redirect URL:', error);
  }
};

const persistUserToStorage = (user) => {
  try {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  } catch (error) {
    console.error('Error persisting user to localStorage:', error);
  }
};

// Initial state
const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  role: null,
  loading: true,
  error: null,
  showSessionTimeoutModal: getSessionTimeoutModalFromStorage(), // Check localStorage on init
  sessionTimeoutVariant: 'timeout',
  initialVerification: true,
  supplierProfile: {},
  supplierProfileError: null, // Tracks refresh failures so UI can show warning badge
  // Kept OUT of supplierProfile on purpose: several reducers replace that object wholesale, which
  // would silently wipe a merged-in letterhead. Loaded once from GET /auth/letterhead - profile
  // responses no longer carry the 50KB-600KB image.
  letterhead: null,
  userPreferences: { alwaysUseProfileAsSupplier: false },
  loginTimestamp: null,
  tokenExpiresAt: null,
  showSessionWarningModal: false,
  // TC-AUTH-008: Concurrent session state
  concurrentSessionDetected: getConcurrentSessionFromStorage(),
  concurrentSessionMessage: getConcurrentSessionMessage(),
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ loginData, bypassToken }, { rejectWithValue }) => {
    try {
      // Prepare headers - include bypass token if provided
      const headers = {};
      if (bypassToken) {
        headers['X-Admin-Bypass'] = bypassToken;
      }

      const response = await api.post('/auth/login', loginData, {
        withCredentials: true, // Important: allows cookies to be set
        headers
      });
      const { user, token, refreshToken, expiresAt, csrfToken } = response.data;

      localStorage.setItem('user', JSON.stringify(user));
      if (csrfToken) {
        sessionStorage.setItem('csrfToken', csrfToken);
      }

      return { user, token, refreshToken, bypassToken, expiresAt, csrfToken };
    } catch (error) {
      // CRITICAL FIX: Enhanced error logging for debugging login failures
      console.error('[authSlice] Login error details:', {
        message: error.message,
        code: error.code,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        } : 'No response (network error)',
        request: error.request ? 'Request was made but no response' : 'Request setup error',
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers
        }
      });
      
      // Provide more specific error messages based on error type
      if (!error.response) {
        // Network error - no response received
        if (error.code === 'ECONNABORTED') {
          return rejectWithValue('Connection timeout. Please check if the server is running.');
        } else if (error.code === 'ERR_NETWORK') {
          return rejectWithValue('Network error. Please check your internet connection and ensure the server is running on port 5000.');
        } else {
          return rejectWithValue('Cannot connect to server. Please ensure the backend server is running.');
        }
      }
      
      // Server responded with error
      const serverMessage = error.response?.data?.message;
      const serverCode = error.response?.data?.code;
      
      if (error.response.status === 401) {
        return rejectWithValue(serverMessage || 'Invalid username or password');
      } else if (error.response.status === 403) {
        return rejectWithValue(serverMessage || 'Access denied. Please contact administrator.');
      } else if (error.response.status === 500) {
        return rejectWithValue(serverMessage || 'Server error. Please try again later.');
      } else if (error.response.status === 503) {
        return rejectWithValue(serverMessage || 'Service temporarily unavailable. Please try again later.');
      }
      
      return rejectWithValue(serverMessage || `Login failed (${error.response.status})`);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    // Call logout API - cookies are automatically sent with withCredentials
    try {
      await api.post('/auth/logout', {}, {
        withCredentials: true,
        _skipAuthErrorHandler: true
      });
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Logout API error:', error);
      }
    }

    // Clear all auth data from localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    localStorage.removeItem('maintenanceBypassToken');
    sessionStorage.removeItem('irbToken');
    sessionStorage.removeItem('wsToken');
    sessionStorage.removeItem('csrfToken'); // SECURITY: Clear CSRF token on logout

    // TC-AUTH-008: Clear concurrent session flags
    localStorage.removeItem(CONCURRENT_SESSION_KEY);
    localStorage.removeItem(CONCURRENT_SESSION_MESSAGE_KEY);

    // Disconnect WebSocket to stop mid-session revocation checks and clear socket state.
    // Dynamic import avoids circular dependency (websocketService imports refreshAccessToken from here).
    try {
      const { default: websocketService } = await import('../services/websocketService');
      websocketService.disconnect();
    } catch (e) {
      // Non-critical — socket will be cleaned up on page unload if disconnect fails
    }

    // Clear state last
    dispatch(logout());

    return null;
  }
);


let wasInitialVerification = false;

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      // Cookies are automatically sent with withCredentials
      // No need to manually send refresh token
      const response = await api.post('/auth/refresh-token', {}, {
        withCredentials: true,
        _skipAuthErrorHandler: true,
        _skipAuthRefresh: true
      });
      const { user, token, refreshToken, expiresAt, csrfToken } = response.data;

      localStorage.setItem('user', JSON.stringify(user));
      if (csrfToken) {
        sessionStorage.setItem('csrfToken', csrfToken);
      }

      wasInitialVerification = true;
      return { user, token, refreshToken, expiresAt, csrfToken };
    } catch (error) {
      if (!wasInitialVerification) {
        return rejectWithValue('Initial verification failed');
      }
      return rejectWithValue('Session expired');
    }
  }
);

export const verifyAuth = createAsyncThunk(
  'auth/verify',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/verify', {
        _skipAuthErrorHandler: true,
        _skipAuthRefresh: true
      });
      return response.data;
    } catch (error) {
      return rejectWithValue('Authentication failed');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile', { supplierProfile: profileData });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

export const updateUserPreferences = createAsyncThunk(
  'auth/updatePreferences',
  async (preferencesData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/preferences', { userPreferences: preferencesData });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update preferences');
    }
  }
);

// NEW: Refresh supplier profile from server for real-time synchronization
// The letterhead is fetched separately from the profile so login and profile refresh stay small.
// Re-dispatch after an upload or delete.
export const fetchLetterhead = createAsyncThunk(
  'auth/fetchLetterhead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/letterhead');
      return response.data?.data?.letterhead || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load letterhead');
    }
  }
);

export const refreshSupplierProfile = createAsyncThunk(
  'auth/refreshSupplierProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/profile');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to refresh profile');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const payload = action.payload || {};
      const hasUser = Object.prototype.hasOwnProperty.call(payload, 'user');
      const hasAccessToken = Object.prototype.hasOwnProperty.call(payload, 'accessToken');
      const hasRefreshToken = Object.prototype.hasOwnProperty.call(payload, 'refreshToken');
      const hasLoginTimestamp = Object.prototype.hasOwnProperty.call(payload, 'loginTimestamp');
      const hasTokenExpiresAt = Object.prototype.hasOwnProperty.call(payload, 'tokenExpiresAt');

      if (hasUser) {
        state.user = payload.user;
        state.role = payload.user?.role || null;
        state.supplierProfile = payload.user?.supplierProfile || {};
        state.userPreferences = payload.user?.userPreferences || { alwaysUseProfileAsSupplier: false };
        persistUserToStorage(payload.user);
      }
      if (hasAccessToken) {
        state.accessToken = payload.accessToken;
      }
      if (hasRefreshToken) {
        state.refreshToken = payload.refreshToken;
      }
      if (hasLoginTimestamp) {
        state.loginTimestamp = payload.loginTimestamp || state.loginTimestamp;
      }
      if (hasTokenExpiresAt) {
        state.tokenExpiresAt = payload.tokenExpiresAt || state.tokenExpiresAt;
      }
      state.loading = false;
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.role = null;
      state.loading = false;
      state.error = null;
      state.letterhead = null;
      state.loginTimestamp = null;
      state.tokenExpiresAt = null;
      state.sessionTimeoutVariant = 'timeout';
      // Clear any pending session-timeout modal + its persisted flag so it cannot reappear on the
      // login page or right after a successful re-login (stale flag was a duplicate-modal source).
      state.showSessionTimeoutModal = false;
      setSessionTimeoutModalInStorage(false);
      setApiAccessToken(null);
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    showSessionTimeout: (state, action) => {
      // Singleton per tab: ignore repeat triggers while the modal is already shown. The same expiry
      // can fire from the axios 401 handler, the auth:sessionExpired event, a WS auth_error, the idle
      // timer, and a stale-flag restore - all dispatching this action. First trigger wins; no stacking.
      if (state.showSessionTimeoutModal) return;
      state.showSessionTimeoutModal = true;
      // Persist to localStorage so modal survives page reloads
      setSessionTimeoutModalInStorage(true);
      // TC-AUTH-008: Support variant ('timeout' or 'auth-error') from action.payload
      state.sessionTimeoutVariant = action.payload?.variant || 'timeout';
    },
    hideSessionTimeout: (state) => {
      state.showSessionTimeoutModal = false;
      // Clear from localStorage when user explicitly closes the modal
      setSessionTimeoutModalInStorage(false);
    },
    resetSessionTimeout: (state) => {
      // This would be handled by middleware/sagas in a full implementation
      // For now, just reset any local timeout logic
    },
    showSessionWarning: (state) => {
      state.showSessionWarningModal = true;
    },
    hideSessionWarning: (state) => {
      state.showSessionWarningModal = false;
    },
    // Set after an upload or delete so the change shows immediately without re-fetching the image.
    setLetterhead: (state, action) => {
      state.letterhead = action.payload || null;
    },
    setSupplierProfile: (state, action) => {
      state.supplierProfile = action.payload || {};
      if (state.user) {
        state.user.supplierProfile = state.supplierProfile;
        persistUserToStorage(state.user);
      }
    },
    setUserPreferences: (state, action) => {
      state.userPreferences = action.payload || { alwaysUseProfileAsSupplier: false };
      if (state.user) {
        state.user.userPreferences = state.userPreferences;
        persistUserToStorage(state.user);
      }
    },
    // TC-AUTH-008: Concurrent session reducers
    setConcurrentSessionDetected: (state, action) => {
      state.concurrentSessionDetected = action.payload;
      if (action.payload) {
        state.concurrentSessionMessage = getConcurrentSessionMessage();
      }
    },
    clearConcurrentSessionState: (state) => {
      state.concurrentSessionDetected = false;
      state.concurrentSessionMessage = '';
      clearConcurrentSession();
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.showSessionTimeoutModal = false;
        state.showSessionWarningModal = false;
        setSessionTimeoutModalInStorage(false);
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, user, refreshToken, bypassToken, expiresAt } = action.payload;
        const loginTimestamp = Date.now();
        const tokenExpiresAt = expiresAt || (loginTimestamp + 3 * 60 * 60 * 1000);
        state.user = user;
        state.accessToken = token;
        state.refreshToken = refreshToken;
        state.role = user.role;
        state.supplierProfile = user.supplierProfile || {};
        state.userPreferences = user.userPreferences || { alwaysUseProfileAsSupplier: false };
        state.loginTimestamp = loginTimestamp;
        state.tokenExpiresAt = tokenExpiresAt;
        state.loading = false;
        state.error = null;
        // CRITICAL FIX: Hide session timeout modal on successful login
        state.showSessionTimeoutModal = false;
        // TC-AUTH-008: Clear concurrent session state on successful login
        state.concurrentSessionDetected = false;
        state.concurrentSessionMessage = '';

        localStorage.setItem('loginTimestamp', loginTimestamp.toString());
        localStorage.setItem('tokenExpiresAt', tokenExpiresAt.toString());

        // TC-AUTH-008: Clear concurrent session flags on successful login
        localStorage.removeItem(CONCURRENT_SESSION_KEY);
        localStorage.removeItem(CONCURRENT_SESSION_MESSAGE_KEY);

        // CRITICAL FIX: Clear session timeout flags on successful login
        // This prevents the session timeout modal from showing again after login
        setSessionTimeoutModalInStorage(false);

        // Reset refresh attempts counter on successful login
        resetRefreshAttempts();
        setApiAccessToken(token);
        // New auth generation: 401s from the previous session's in-flight requests are now ignored
        // so they cannot re-open the session-timeout modal right after this successful login.
        bumpAuthGeneration();

        // Store token in sessionStorage for WebSocket auth
        if (token) {
          sessionStorage.setItem('wsToken', token);
        }

      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Log authentication error to console for debugging
        console.error('[authSlice] Login failed:', action.payload);
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.role = null;
        state.loading = false;
        state.error = null;
        state.letterhead = null;
        // NOTE: We do NOT clear showSessionTimeoutModal here
        // It should persist until user explicitly closes it
        // The localStorage flag will be cleared when user clicks "Log In Again"
      })
      // Refresh token
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        const { token, user, refreshToken } = action.payload;
        state.accessToken = token;
        state.user = user;
        state.refreshToken = refreshToken;
        state.role = user.role;
        state.supplierProfile = user.supplierProfile || {};
        state.userPreferences = user.userPreferences || { alwaysUseProfileAsSupplier: false };
        state.loading = false;
        state.error = null;
        state.initialVerification = false; // Initial verification complete
        
        const newLoginTimestamp = Date.now();
        const newTokenExpiresAt = action.payload.expiresAt || (newLoginTimestamp + 3 * 60 * 60 * 1000);
        state.loginTimestamp = newLoginTimestamp;
        state.tokenExpiresAt = newTokenExpiresAt;
        localStorage.setItem('loginTimestamp', newLoginTimestamp.toString());
        localStorage.setItem('tokenExpiresAt', newTokenExpiresAt.toString());

        // Store token in sessionStorage for WebSocket auth
        if (token) {
          sessionStorage.setItem('wsToken', token);
        }
        setApiAccessToken(token);
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        const wasInitialVerification = state.initialVerification; // capture BEFORE changing
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.role = null;
        state.loading = false;
        // Only show modal if we were ALREADY past initial verification (mid-session expiry).
        // If wasInitialVerification === true (still in initial load): silently clear state, no modal.
        // If wasInitialVerification === false (mid-session): show modal.
        state.initialVerification = false;
        if (wasInitialVerification === false) {
          state.showSessionTimeoutModal = true;
          setSessionTimeoutModalInStorage(true);
        }
      })
      // Verify auth
      .addCase(verifyAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.initialVerification = false; // Initial verification complete
        // Update user data if needed
      })
      .addCase(verifyAuth.rejected, (state) => {
        // Don't clear user state yet - wait for refresh attempt
        // Keep initialVerification true until refresh completes
      })
      // Update profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        // CRITICAL FIX: The thunk returns response.data.data which contains { supplierProfile, userPreferences }
        // action.payload is already the data object, not response.data
        const updatedProfile = action.payload?.supplierProfile || {};
        state.supplierProfile = updatedProfile;
        // Also update user.supplierProfile to keep them in sync
        if (state.user) {
          state.user.supplierProfile = updatedProfile;
          persistUserToStorage(state.user);
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update preferences
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.userPreferences = action.payload.userPreferences || { alwaysUseProfileAsSupplier: false };
        if (state.user) {
          state.user.userPreferences = state.userPreferences;
          persistUserToStorage(state.user);
        }
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Refresh supplier profile for real-time synchronization
      .addCase(fetchLetterhead.fulfilled, (state, action) => {
        state.letterhead = action.payload || null;
      })
      .addCase(fetchLetterhead.rejected, (state) => {
        // Non-fatal: every display surface falls back gracefully when there is no letterhead.
        state.letterhead = null;
      })
      .addCase(refreshSupplierProfile.fulfilled, (state, action) => {
        const refreshedProfile = action.payload?.supplierProfile || {};
        state.supplierProfile = refreshedProfile;
        state.supplierProfileError = null; // Clear error on success
        // Also update user.supplierProfile to keep them in sync
        if (state.user) {
          state.user.supplierProfile = refreshedProfile;
        }
        // Update userPreferences if also returned
        if (action.payload?.userPreferences) {
          state.userPreferences = action.payload.userPreferences;
          if (state.user) {
            state.user.userPreferences = state.userPreferences;
          }
        }
        if (state.user) {
          persistUserToStorage(state.user);
        }
      })
      .addCase(refreshSupplierProfile.rejected, (state, action) => {
        // Keep existing profile — but surface error so UI can show warning badge
        state.supplierProfileError = action.payload || 'Failed to refresh supplier profile';
        console.error('Failed to refresh supplier profile:', action.payload);
      });
  },
});

export const {
  setCredentials,
  logout,
  setLoading,
  setError,
  showSessionTimeout,
  hideSessionTimeout,
  resetSessionTimeout,
  showSessionWarning,
  hideSessionWarning,
  setSupplierProfile,
  setLetterhead,
  setUserPreferences,
  // TC-AUTH-008: Export concurrent session actions
  setConcurrentSessionDetected,
  clearConcurrentSessionState,
} = authSlice.actions;

export default authSlice.reducer;
